import React, { useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import SwalAlert from "../../components/common/alert";
import FileUploadGrid from "../../components/upload-file";
import DataAutoComplete from "../../components/auto-complete";
import documentType from "../../data/documentType";
import { callGetAllProvince, callGetDistrict, callSearchAgentInfo } from "../../api/settings/service";
import { callGetActivePackage } from "../../api/contract/service";
import {
  callCreateLegacyLifeEntryDraft,
  callGetLegacyLifeEntryById,
  callSubmitLegacyLifeEntry,
  callUpdateLegacyLifeEntryDraft,
  callUploadLegacyLifeEntryFiles,
} from "../../api/legacy-life-entry/service";
import { calculateAge, FormatCurrencys, relation } from "../../utils/commonUtil";
import "../../components/i18n/lang";
import "./legacy-life-entry.css";
import "./legacy-life-entry-v6.css";

const PROMOTIONS = {
  NONE: 'NONE',
  ENTRY_FREE_FIRST_MONTH_REGISTRATION: 'ENTRY_FREE_FIRST_MONTH_REGISTRATION',
  PAY_12_FREE_1: 'PAY_12_FREE_1',
};

const emptyPerson = () => ({
  firstName: "", lastName: "", gender: "m", dateOfBirth: "", age: 0,
  documentTypeId: "", documentNo: "", issueDate: "", expiredDate: "", issuePlace: "",
  phone: "", provinceId: "", districtId: "", village: "", homeNumber: "", unitNumber: "",
  documentImagePath: [], file: null, fileDelete: [],
});

const createInitialData = () => ({
  contract: {
    contractNumber: "", entryDate: "", package: "", packageId: "",
    packageMoneyOptionId: "", agentId: "", agentCode: "", payPerMonth: "",
    contractBookNumber: "", remark: "",
  },
  customer: { ...emptyPerson(), isVerify: 0 },
  beneficiaries: [{ ...emptyPerson(), relation: "" }],
  payments: [],
  documents: [],
});

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const parsed = moment(value, [moment.ISO_8601, "YYYY-MM-DD", "DD/MM/YYYY"], true);
  return parsed.isValid() ? parsed.toDate() : null;
};
const dateValue = (date) => {
  if (!date) return "";
  const parsed = moment(date);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
};

// API responses in the existing project are not always the same shape.
// Keep every select safe and compatible with both array and wrapped-list responses.
const normalizeList = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.result)) return value.result;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.rows)) return value.rows;
  return [];
};

export default function LegacyLifeEntry() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const fromList = Boolean(location.state?.fromList);
  const { id } = useParams();
  const [data, setData] = useState(createInitialData());
  const [files, setFiles] = useState([]);
  const [billFiles, setBillFiles] = useState({});
  const [entryId, setEntryId] = useState(id || null);
  const [provinceList, setProvinceList] = useState([]);
  const [districtList, setDistrictList] = useState({ customer: [], beneficiaries: {} });
  const [packageList, setPackageList] = useState([]);
  const [agent, setAgent] = useState([]);
  const [agentData, setAgentData] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const init = async () => {
      const [provinceRsp, packageRsp] = await Promise.all([
        dispatch(callGetAllProvince()),
        dispatch(callGetActivePackage()),
      ]);
      if (provinceRsp?.payload?.responseCode === "00000") setProvinceList(normalizeList(provinceRsp.payload.data));
      if (packageRsp?.payload?.responseCode === "00000") setPackageList(normalizeList(packageRsp.payload.data));
    };
    init();
  }, [dispatch]);

  useEffect(() => {
    if (!id) return;
    const loadData = async () => {
      const rsp = await dispatch(callGetLegacyLifeEntryById(id));
      if (rsp?.payload?.responseCode !== "00000" || !rsp?.payload?.data?.payload) return;
      const payload = rsp.payload.data.payload;
      setData({ ...createInitialData(), ...payload });
      if (payload.contract?.agentId) setAgentData({ agentCode: payload.contract.agentId });
      if (payload.customer?.provinceId) loadDistrict(payload.customer.provinceId, "customer");
      (payload.beneficiaries || []).forEach((item, index) => {
        if (item.provinceId) loadDistrict(item.provinceId, "beneficiary", index);
      });
    };
    loadData();
  }, [dispatch, id]);

  const loadDistrict = async (provinceId, type, index = 0) => {
    if (!provinceId) return;
    const rsp = await dispatch(callGetDistrict({ provinceId }));
    if (rsp?.payload?.responseCode !== "00000") return;
    setDistrictList((current) => type === "customer"
      ? { ...current, customer: normalizeList(rsp.payload.data) }
      : { ...current, beneficiaries: { ...current.beneficiaries, [index]: normalizeList(rsp.payload.data) } });
  };

  const setContract = (key, value) => setData((current) => ({ ...current, contract: { ...current.contract, [key]: value } }));
  const setPerson = (key, value) => setData((current) => ({ ...current, customer: { ...current.customer, [key]: value } }));
  const setPersonDate = (key, date) => setData((current) => ({
    ...current,
    customer: {
      ...current.customer,
      [key]: dateValue(date),
      ...(key === "dateOfBirth" ? { age: calculateAge(dateValue(date)) } : {}),
    },
  }));

  const changeCustomer = (key, value) => {
    setPerson(key, value);
    if (key === "provinceId") {
      setPerson("districtId", "");
      loadDistrict(value, "customer");
    }
  };

  const addBeneficiary = () => setData((current) => ({
    ...current,
    beneficiaries: [...current.beneficiaries, { ...emptyPerson(), relation: "" }],
  }));
  const removeBeneficiary = (index) => setData((current) => ({
    ...current,
    beneficiaries: current.beneficiaries.filter((_, i) => i !== index),
  }));
  const setBeneficiary = (index, key, value) => {
    setData((current) => ({
      ...current,
      beneficiaries: current.beneficiaries.map((item, i) => i === index ? { ...item, [key]: value } : item),
    }));
    if (key === "provinceId") {
      setData((current) => ({
        ...current,
        beneficiaries: current.beneficiaries.map((item, i) => i === index ? { ...item, districtId: "" } : item),
      }));
      loadDistrict(value, "beneficiary", index);
    }
  };
  const setBeneficiaryDate = (index, key, date) => setBeneficiary(index, key, dateValue(date));

  const loadAgentData = async (value) => {
    const request = value?.id
      ? { keyword: null, page: 1, maxResult: 1, agentId: value.id, type: 1 }
      : { keyword: value, page: 1, maxResult: 10, agentId: null, type: 1 };
    const rsp = await dispatch(callSearchAgentInfo(request));
    if (rsp?.payload?.responseCode === "00000") {
      setAgent(rsp?.payload?.data?.result || []);
    }
  };

  const onSelectAgent = (item) => {
    if (!item) return;
    setData((current) => ({
      ...current,
      contract: {
        ...current.contract,
        agentId: item.agentId,
        agentCode: item.agentCode || item.agentId || "",
      },
    }));
  };

  const selectPackage = (value) => {
    if (!value) {
      setData((current) => ({ ...current, contract: { ...current.contract, package: "", packageId: "", packageMoneyOptionId: "", payPerMonth: "" } }));
      return;
    }
    const [packageId, optionId] = value.split("_");
    const selected = packageList.find((item) => String(item.id) === String(packageId) && String(item.packageMoneyOptionId) === String(optionId));
    if (selected && data.customer.age && (data.customer.age < selected.ageForm || data.customer.age > selected.ageTo)) {
      SwalAlert.error(t("legacyLifeEntry.validationTitle"), t("errors.ageNotPermis"));
      return;
    }
    setData((current) => ({
      ...current,
      contract: {
        ...current.contract,
        package: value,
        packageId,
        packageMoneyOptionId: optionId,
        payPerMonth: selected?.payPerMonth ?? current.contract.payPerMonth,
      },
    }));
  };

  const addBill = () => setData((current) => ({
    ...current,
    payments: [...current.payments, { billNumber: "", paidDate: "", monthCount: 1, promotionCode: PROMOTIONS.NONE, totalAmount: 0, documents: [] }],
  }));
  const removeBill = (index) => setData((current) => ({ ...current, payments: current.payments.filter((_, i) => i !== index) }));
  const setBill = (index, key, value) => setData((current) => ({
    ...current,
    payments: current.payments.map((item, i) => {
      if (i !== index) return item;
      const next = { ...item, [key]: value };
      if (key === "monthCount" || key === "promotionCode") {
        const months = Number(key === "monthCount" ? value : next.monthCount || 0);
        const promotionCode = key === "promotionCode" ? value : next.promotionCode;
        const payPerMonth = Number(current.contract.payPerMonth || 0);
        const gross = months * payPerMonth;
        next.totalAmount = promotionCode === PROMOTIONS.NONE ? gross : Math.max(0, gross - payPerMonth);
      }
      return next;
    }),
  }));

  const required = (value) => value !== null && value !== undefined && String(value).trim() !== "";
  const validateAll = () => {
    const next = {};
    {
      ["contractNumber", "entryDate", "packageId", "packageMoneyOptionId", "payPerMonth", "agentId"].forEach((key) => {
        if (!required(data.contract[key])) next[`contract.${key}`] = true;
      });
    }
    {
      ["firstName", "gender", "dateOfBirth", "phone", "provinceId", "districtId", "village", "documentTypeId", "documentNo"].forEach((key) => {
        if (!required(data.customer[key])) next[`customer.${key}`] = true;
      });
      if (data.customer.phone && !/^[0-9]{7,15}$/.test(String(data.customer.phone))) next["customer.phone"] = true;
      if (data.customer.issueDate && data.customer.expiredDate && data.customer.issueDate > data.customer.expiredDate) next["customer.expiredDate"] = true;
    }
    {
      if (!data.beneficiaries.length) next.beneficiaries = true;
      data.beneficiaries.forEach((item, index) => {
        ["firstName", "lastName", "gender", "relation", "documentTypeId", "documentNo", "phone", "provinceId", "districtId", "village"].forEach((key) => {
          if (!required(item[key])) next[`beneficiaries.${index}.${key}`] = true;
        });
        if (item.phone && !/^[0-9]{7,15}$/.test(String(item.phone))) next[`beneficiaries.${index}.phone`] = true;
        if (item.issueDate && item.expiredDate && item.issueDate > item.expiredDate) next[`beneficiaries.${index}.expiredDate`] = true;
      });
    }
    {
      // Receipt is optional when creating a legacy contract. Validate only when a receipt exists.
      data.payments.forEach((item, index) => {
        ["billNumber", "paidDate", "monthCount"].forEach((key) => {
          if (!required(item[key]) || (key === "monthCount" && Number(item[key]) <= 0)) {
            next[`payments.${index}.${key}`] = true;
          }
        });
      });
    }
    setErrors(next);
    if (Object.keys(next).length) {
      SwalAlert.error(t("legacyLifeEntry.validationTitle"), t("legacyLifeEntry.validationMessage"));
      return false;
    }
    return true;
  };

  const validateDraft = () => {
    const next = {};
    if (!required(data.contract.contractNumber)) next["contract.contractNumber"] = true;
    if (!required(data.contract.entryDate)) next["contract.entryDate"] = true;
    setErrors(next);
    if (Object.keys(next).length) {
      SwalAlert.error(t("legacyLifeEntry.validationTitle"), t("legacyLifeEntry.draftValidationMessage"));
      return false;
    }
    return true;
  };

  const uploadBillDocuments = async (payments) => {
    const nextPayments = [];
    for (let index = 0; index < payments.length; index += 1) {
      const bill = payments[index];
      const localFiles = billFiles[index] || [];
      if (!localFiles.length) { nextPayments.push(bill); continue; }
      const reference = bill.billNumber || `${data.contract.contractNumber || "draft"}_bill_${index + 1}`;
      const rsp = await dispatch(callUploadLegacyLifeEntryFiles({ contractNumber: reference, files: localFiles }));
      nextPayments.push({ ...bill, documents: [...(bill.documents || []), ...(rsp?.payload?.data || [])] });
    }
    return nextPayments;
  };

  const uploadFiles = async () => {
    if (!files.length) return data.documents || [];
    const rsp = await dispatch(callUploadLegacyLifeEntryFiles({ contractNumber: data.contract.contractNumber || "draft", files }));
    return [...(data.documents || []), ...(rsp?.payload?.data || [])];
  };

  const save = async (submit = false) => {
    if (submit ? !validateAll() : !validateDraft()) return;
    const documents = await uploadFiles();
    const payments = await uploadBillDocuments(data.payments);
    const payload = { ...data, documents, payments };
    let currentId = entryId;
    let rsp;
    if (currentId) rsp = await dispatch(callUpdateLegacyLifeEntryDraft({ id: currentId, data: payload }));
    else {
      rsp = await dispatch(callCreateLegacyLifeEntryDraft(payload));
      if (rsp?.payload?.responseCode === "00000") {
        currentId = rsp?.payload?.data?.id;
        setEntryId(currentId);
        navigate("/admin/legacyLifeEntry/list")
      }
    }
    if (rsp?.payload?.responseCode !== "00000") return;
    setData(payload); setFiles([]);
    if (submit && currentId) {
      const submitRsp = await dispatch(callSubmitLegacyLifeEntry(currentId));
      if (submitRsp?.payload?.responseCode === "00000") navigate("/admin/legacyLifeEntry/list");
    }
  };

  const total = useMemo(() => data.payments.reduce((sum, item) => sum + Number(item.totalAmount || 0), 0), [data.payments]);
  const monthCount = useMemo(() => data.payments.reduce((sum, item) => sum + Number(item.monthCount || 0), 0), [data.payments]);
  const err = (path) => errors[path] ? " legacy-invalid" : "";

  const DateField = ({ label, value, onChange, path, requiredMark = false }) => (
    <div className="legacy-field">
      <label>{label}{requiredMark && <span className="text-danger"> *</span>}</label>
      <DatePicker selected={toDate(value)} onChange={onChange} dateFormat="dd/MM/yyyy" placeholderText={label} className={`form-control-uld${err(path)}`} />
    </div>
  );

  return (
    <div className="legacy-wrap legacy-modern legacy-v5-contract-page">
      <div className="legacy-v5-contract-head">
        <div className="legacy-v5-contract-head-main">
          {fromList && <button type="button" className="legacy-back-button" onClick={() => navigate("/admin/legacyLifeEntry/list")}>← {t("legacyLifeEntry.back")}</button>}
          <div className="legacy-v5-contract-mark">LTI</div>
          <div>
            <div className="legacy-v5-eyebrow">LEGACY LIFE CONTRACT</div>
            <h2>{t("legacyLifeEntry.title")}</h2>
            <p>{t("legacyLifeEntry.description")}</p>
          </div>
        </div>
        <div className="legacy-v5-contract-head-info">
          <div><span>{t("legacyLifeEntry.contractNumber")}</span><b>{data.contract.contractNumber || "-"}</b></div>
          <div><span>{t("legacyLifeEntry.bills")}</span><b>{data.payments.length}</b></div>
          <button type="button" onClick={() => save(false)}>{t("legacyLifeEntry.saveDraft")}</button>
        </div>
      </div>
      <div className="legacy-section-nav">
        <a href="#legacy-customer">01 · {t("legacyLifeEntry.customerInfo")}</a>
        <a href="#legacy-contract">02 · {t("legacyLifeEntry.contractInfo")}</a>
        <a href="#legacy-agent">03 · {t("legacyLifeEntry.agentSection")}</a>
        <a href="#legacy-beneficiaries">04 · {t("legacyLifeEntry.beneficiaries")}</a>
        <a href="#legacy-payments">05 · {t("legacyLifeEntry.paymentHistory")}</a>
        <a href="#legacy-documents">06 · {t("legacyLifeEntry.documents")}</a>
      </div>
      <section id="legacy-customer" className="legacy-card"><div className="legacy-card-title"><div><span>01</span><h4>{t("legacyLifeEntry.customerInfo")}</h4></div></div><div className="legacy-form-grid">
        <div className="legacy-field"><label>{t("contracts.life.gender")} </label><select className="form-select" value={data.customer.gender} onChange={(e) => changeCustomer("gender", e.target.value)}><option value="m">{t("gender.m")}</option><option value="f">{t("gender.f")}</option></select></div>
        <div className="legacy-field"><label>{t("contracts.life.firstName")} <span className="text-danger">*</span></label><input className={`form-control-uld${err("customer.firstName")}`} value={data.customer.firstName} onChange={(e) => changeCustomer("firstName", e.target.value)} /></div>
        <div className="legacy-field"><label>{t("contracts.life.lastName")}</label><input className="form-control-uld" value={data.customer.lastName} onChange={(e) => changeCustomer("lastName", e.target.value)} /></div>
        <DateField label={`${t("contracts.life.dateOfBirth")} · ${t("uiLabels.ageYears", { age: data.customer.age || 0 })}`} value={data.customer.dateOfBirth} onChange={(date) => setPersonDate("dateOfBirth", date)} path="customer.dateOfBirth" requiredMark />
        <div className="legacy-field"><label>{t("contracts.life.phone")} <span className="text-danger">*</span></label><input inputMode="numeric" maxLength="15" className={`form-control-uld${err("customer.phone")}`} value={data.customer.phone} onChange={(e) => changeCustomer("phone", e.target.value.replace(/\D/g, ""))} /></div>
        <div className="legacy-field"><label>{t("contracts.life.provinceName")} <span className="text-danger">*</span></label><select className={`form-select${err("customer.provinceId")}`} value={data.customer.provinceId} onChange={(e) => changeCustomer("provinceId", e.target.value)}><option value="">{t("pleaseSelect")}</option>{normalizeList(provinceList).map((item) => <option key={item.provinceId} value={item.provinceId}>{item.provinceName}</option>)}</select></div>
        <div className="legacy-field"><label>{t("contracts.life.districtName")} <span className="text-danger">*</span></label><select className={`form-select${err("customer.districtId")}`} value={data.customer.districtId} onChange={(e) => changeCustomer("districtId", e.target.value)} disabled={!data.customer.provinceId}><option value="">{t("pleaseSelect")}</option>{normalizeList(districtList.customer).map((item) => <option key={item.districtId} value={item.districtId}>{item.districtName}</option>)}</select></div>
        <div className="legacy-field"><label>{t("contracts.life.village")} <span className="text-danger">*</span></label><input className={`form-control-uld${err("customer.village")}`} value={data.customer.village} onChange={(e) => changeCustomer("village", e.target.value)} /></div>
        <div className="legacy-field"><label>{t("contracts.life.homeNumber")}</label><input className="form-control-uld" value={data.customer.homeNumber} onChange={(e) => changeCustomer("homeNumber", e.target.value)} /></div>
        <div className="legacy-field"><label>{t("contracts.life.unitNumber")}</label><input className="form-control-uld" value={data.customer.unitNumber} onChange={(e) => changeCustomer("unitNumber", e.target.value)} /></div>
        <div className="legacy-field"><label>{t("contracts.life.documentType")} <span className="text-danger">*</span></label><select className={`form-select${err("customer.documentTypeId")}`} value={data.customer.documentTypeId} onChange={(e) => changeCustomer("documentTypeId", e.target.value)}><option value="">{t("pleaseSelect")}</option>{documentType.map((item) => <option key={item.id} value={item.id}>{t(item.name)}</option>)}</select></div>
        <div className="legacy-field"><label>{t("contracts.life.documentNo")} <span className="text-danger">*</span></label><input className={`form-control-uld${err("customer.documentNo")}`} value={data.customer.documentNo} onChange={(e) => changeCustomer("documentNo", e.target.value)} /></div>
        <DateField label={t("contracts.life.issueDate")} value={data.customer.issueDate} onChange={(date) => setPersonDate("issueDate", date)} path="customer.issueDate" />
        <DateField label={t("contracts.life.expiredDate")} value={data.customer.expiredDate} onChange={(date) => setPersonDate("expiredDate", date)} path="customer.expiredDate" />
        <div className="legacy-field"><label>{t("contracts.life.issuePlace")}</label><input className="form-control-uld" value={data.customer.issuePlace} onChange={(e) => changeCustomer("issuePlace", e.target.value)} /></div>
        <div className="legacy-field"><label>{t("contracts.life.isVerify")}</label><select className="form-select" value={data.customer.isVerify} onChange={(e) => changeCustomer("isVerify", e.target.value)}><option value={0}>{t("verifyStatus.notVerify")}</option><option value={1}>{t("verifyStatus.verify")}</option></select></div>
      </div></section>

      <section id="legacy-contract" className="legacy-card"><div className="legacy-card-title"><div><span>02</span><h4>{t("legacyLifeEntry.contractInfo")}</h4></div><small>{t("legacyLifeEntry.requiredHint")}</small></div><div className="legacy-form-grid">
        <div className="legacy-field"><label>{t("legacyLifeEntry.contractNumber")} <span className="text-danger">*</span></label><input className={`form-control-uld${err("contract.contractNumber")}`} value={data.contract.contractNumber} onChange={(e) => setContract("contractNumber", e.target.value)} /></div>
        <DateField label={t("legacyLifeEntry.entryDate")} value={data.contract.entryDate} onChange={(date) => setContract("entryDate", dateValue(date))} path="contract.entryDate" requiredMark />
        <div className="legacy-field legacy-span2"><label>{t("contracts.life.package")} <span className="text-danger">*</span></label><select className={`form-select${err("contract.packageId")}`} value={data.contract.package || ""} onChange={(e) => selectPackage(e.target.value)}><option value="">{t("pleaseSelect")}</option>{normalizeList(packageList).map((item, index) => <option key={`${item.id}-${item.packageMoneyOptionId}-${index}`} value={`${item.id}_${item.packageMoneyOptionId}`}>{item.ageRange} {t("title.year")} · {FormatCurrencys(item.payPerMonth)} {t("currentcy.kip")}/{t("title.month")}</option>)}</select></div>
        <div className="legacy-field legacy-span3"><label>{t("legacyLifeEntry.remark")}</label><textarea className="form-control-uld" rows="3" value={data.contract.remark} onChange={(e) => setContract("remark", e.target.value)} /></div>
      </div></section>



      <section id="legacy-agent" className="legacy-card">
        <div className="legacy-card-title"><div><span>03</span><h4>{t("legacyLifeEntry.agentSection")}</h4></div></div>
        <div className="legacy-form-grid">
          <div className="legacy-field legacy-agent-field legacy-span2">
            <label>{t("contracts.life.agentCode")} <span className="text-danger">*</span></label>
            <DataAutoComplete label="firstName" id="agentCode" initialData={agentData} data={agent} autoSelect={true} placeholder={t("legacyLifeEntry.searchAgent")} handleChange={loadAgentData} onSelect={onSelectAgent} />
            {data.contract.agentId && <div className="legacy-agent-selected"><span>{t("legacyLifeEntry.selectedAgent")}</span><strong>{data.contract.agentCode || data.contract.agentId}</strong><button type="button" onClick={() => { setAgentData(null); setData((current) => ({ ...current, contract: { ...current.contract, agentId: "", agentCode: "" } })); }}>{t("legacyLifeEntry.changeAgent")}</button></div>}
          </div>
        </div>
      </section>

      <section id="legacy-beneficiaries" className="legacy-card"><div className="legacy-card-title"><div><span>04</span><h4>{t("legacyLifeEntry.beneficiaries")}</h4></div><button type="button" className="legacy-btn-secondary" onClick={addBeneficiary}>+ {t("legacyLifeEntry.add")}</button></div>{data.beneficiaries.map((item, index) => <div className="legacy-person-card" key={`beneficiary-${index}`}><div className="legacy-person-head"><b>{t("legacyLifeEntry.beneficiaryNo", { number: index + 1 })}</b>{data.beneficiaries.length > 1 && <button onClick={() => removeBeneficiary(index)}>{t("legacyLifeEntry.remove")}</button>}</div><div className="legacy-form-grid">
        <div className="legacy-field"><label>{t("contracts.life.gender")} <span className="text-danger">*</span></label><select className="form-select" value={item.gender} onChange={(e) => setBeneficiary(index, "gender", e.target.value)}><option value="m">{t("gender.m")}</option><option value="f">{t("gender.f")}</option></select></div>
        <div className="legacy-field"><label>{t("contracts.life.firstName")} <span className="text-danger">*</span></label><input className={`form-control-uld${err(`beneficiaries.${index}.firstName`)}`} value={item.firstName} onChange={(e) => setBeneficiary(index, "firstName", e.target.value)} /></div>
        <div className="legacy-field"><label>{t("contracts.life.lastName")} <span className="text-danger">*</span></label><input className={`form-control-uld${err(`beneficiaries.${index}.lastName`)}`} value={item.lastName} onChange={(e) => setBeneficiary(index, "lastName", e.target.value)} /></div>
        <div className="legacy-field"><label>{t("contracts.life.relation")} <span className="text-danger">*</span></label><select className={`form-select${err(`beneficiaries.${index}.relation`)}`} value={item.relation} onChange={(e) => setBeneficiary(index, "relation", e.target.value)}><option value="">{t("pleaseSelect")}</option>{relation().map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}</select></div>
        <div className="legacy-field"><label>{t("contracts.life.age")}</label><input type="number" className="form-control-uld" value={item.age || ""} onChange={(e) => setBeneficiary(index, "age", e.target.value)} /></div>
        <div className="legacy-field"><label>{t("contracts.life.phone")} <span className="text-danger">*</span></label><input inputMode="numeric" maxLength="15" className={`form-control-uld${err(`beneficiaries.${index}.phone`)}`} value={item.phone} onChange={(e) => setBeneficiary(index, "phone", e.target.value.replace(/\D/g, ""))} /></div>
        <div className="legacy-field"><label>{t("contracts.life.documentType")} <span className="text-danger">*</span></label><select className={`form-select${err(`beneficiaries.${index}.documentTypeId`)}`} value={item.documentTypeId} onChange={(e) => setBeneficiary(index, "documentTypeId", e.target.value)}><option value="">{t("pleaseSelect")}</option>{documentType.map((d) => <option key={d.id} value={d.id}>{t(d.name)}</option>)}</select></div>
        <div className="legacy-field"><label>{t("contracts.life.documentNo")} <span className="text-danger">*</span></label><input className={`form-control-uld${err(`beneficiaries.${index}.documentNo`)}`} value={item.documentNo} onChange={(e) => setBeneficiary(index, "documentNo", e.target.value)} /></div>
        <DateField label={t("contracts.life.issueDate")} value={item.issueDate} onChange={(date) => setBeneficiaryDate(index, "issueDate", date)} path={`beneficiaries.${index}.issueDate`} />
        <DateField label={t("contracts.life.expiredDate")} value={item.expiredDate} onChange={(date) => setBeneficiaryDate(index, "expiredDate", date)} path={`beneficiaries.${index}.expiredDate`} />
        <div className="legacy-field"><label>{t("contracts.life.issuePlace")}</label><input className="form-control-uld" value={item.issuePlace} onChange={(e) => setBeneficiary(index, "issuePlace", e.target.value)} /></div>
        <div className="legacy-field"><label>{t("contracts.life.provinceName")} <span className="text-danger">*</span></label><select className={`form-select${err(`beneficiaries.${index}.provinceId`)}`} value={item.provinceId} onChange={(e) => setBeneficiary(index, "provinceId", e.target.value)}><option value="">{t("pleaseSelect")}</option>{normalizeList(provinceList).map((p) => <option key={p.provinceId} value={p.provinceId}>{p.provinceName}</option>)}</select></div>
        <div className="legacy-field"><label>{t("contracts.life.districtName")} <span className="text-danger">*</span></label><select className={`form-select${err(`beneficiaries.${index}.districtId`)}`} value={item.districtId} onChange={(e) => setBeneficiary(index, "districtId", e.target.value)} disabled={!item.provinceId}><option value="">{t("pleaseSelect")}</option>{normalizeList(districtList.beneficiaries?.[index]).map((d) => <option key={d.districtId} value={d.districtId}>{d.districtName}</option>)}</select></div>
        <div className="legacy-field"><label>{t("contracts.life.village")} <span className="text-danger">*</span></label><input className={`form-control-uld${err(`beneficiaries.${index}.village`)}`} value={item.village} onChange={(e) => setBeneficiary(index, "village", e.target.value)} /></div>
        <div className="legacy-field"><label>{t("contracts.life.homeNumber")}</label><input className="form-control-uld" value={item.homeNumber} onChange={(e) => setBeneficiary(index, "homeNumber", e.target.value)} /></div>
        <div className="legacy-field"><label>{t("contracts.life.unitNumber")}</label><input className="form-control-uld" value={item.unitNumber} onChange={(e) => setBeneficiary(index, "unitNumber", e.target.value)} /></div>
      </div></div>)}</section>

      <section id="legacy-payments" className="legacy-card">
        <div className="legacy-card-title">
          <div><span>05</span><h4>{t("legacyLifeEntry.paymentHistory")}</h4></div>
          <button type="button" className="legacy-btn-secondary" onClick={addBill}>+ {t("legacyLifeEntry.addBill")}</button>
        </div>
        {data.payments.map((item, index) => (
          <div className="legacy-bill-card" key={`bill-${index}`}>
            <div className="legacy-person-head">
              <b>{t("legacyLifeEntry.billNo", { number: index + 1 })}</b>
              <button type="button" onClick={() => removeBill(index)}>{t("legacyLifeEntry.remove")}</button>
            </div>
            <div className="legacy-form-grid legacy-grid3">
              <div className="legacy-field">
                <label>{t("legacyLifeEntry.billNumber")} <span className="text-danger">*</span></label>
                <input className={`form-control-uld${err(`payments.${index}.billNumber`)}`} value={item.billNumber} onChange={(e) => setBill(index, "billNumber", e.target.value)} />
              </div>
              <DateField label={t("legacyLifeEntry.paidDate")} value={item.paidDate} onChange={(date) => setBill(index, "paidDate", dateValue(date))} path={`payments.${index}.paidDate`} requiredMark />
              <div className="legacy-field">
                <label>{t("legacyLifeEntry.monthCount")} <span className="text-danger">*</span></label>
                <input type="number" min="1" max="240" className={`form-control-uld${err(`payments.${index}.monthCount`)}`} value={item.monthCount} onChange={(e) => setBill(index, "monthCount", e.target.value)} />
              </div>
            </div>
            <div className="legacy-promotion-block legacy-promotion-compact">
              <div className="legacy-promotion-heading"><div><b>{t("legacyLifeEntry.promotionTitle")}</b><small>{t("legacyLifeEntry.promotionHint")}</small></div></div>
              <div className="legacy-promotion-grid">
                <button type="button" className={`legacy-promo-option${(item.promotionCode || PROMOTIONS.NONE) === PROMOTIONS.NONE ? " active" : ""}`} onClick={() => setBill(index, "promotionCode", PROMOTIONS.NONE)}><span className="legacy-promo-radio" /><div><b>{t("legacyLifeEntry.promoNone")}</b></div></button>
                <button type="button" className={`legacy-promo-option${item.promotionCode === PROMOTIONS.ENTRY_FREE_FIRST_MONTH_REGISTRATION ? " active" : ""}`} onClick={() => setBill(index, "promotionCode", PROMOTIONS.ENTRY_FREE_FIRST_MONTH_REGISTRATION)}><span className="legacy-promo-radio" /><div><b>{t("legacyLifeEntry.promoEntryFree")}</b><small>{t("legacyLifeEntry.promoEntryFreeDesc")}</small></div></button>
                <button type="button" className={`legacy-promo-option${item.promotionCode === PROMOTIONS.PAY_12_FREE_1 ? " active" : ""}`} onClick={() => { if (Number(item.monthCount || 0) < 12) setBill(index, "monthCount", 12); setBill(index, "promotionCode", PROMOTIONS.PAY_12_FREE_1); }}><span className="legacy-promo-radio" /><div><b>{t("legacyLifeEntry.promo12Free1")}</b><small>{t("legacyLifeEntry.promo12Free1Desc")}</small></div></button>
              </div>
            </div>
            <div className="legacy-periods">
              {Number(item.monthCount || 0)} {t("legacyLifeEntry.months")} · {Number(item.totalAmount || 0).toLocaleString()} LAK
            </div>
            <div className="legacy-bill-documents">
              <label>{t("legacyLifeEntry.billDocuments")}</label>
              <FileUploadGrid multiple={false} accept="image/*,.pdf" filesFromApi={item.documents || []} onChange={(value) => setBillFiles((current) => ({ ...current, [index]: value.files || [] }))} folderName="legacy_life_bill" referenceName={item.billNumber || `${data.contract.contractNumber || "draft"}_bill_${index + 1}`} />
            </div>
          </div>
        ))}
        <div className="legacy-total"><span>{t("legacyLifeEntry.grandTotal")}</span><strong>{total.toLocaleString()} LAK</strong></div>
      </section>

      <section id="legacy-documents" className="legacy-card"><div className="legacy-card-title"><div><span>06</span><h4>{t("legacyLifeEntry.documents")}</h4></div></div><FileUploadGrid multiple={false} accept="image/*,.pdf" filesFromApi={data.documents || []} onChange={(value) => setFiles(value.files || [])} folderName="legacy_life_contract" referenceName={data.contract.contractNumber || "draft"} /></section>

      <section className="legacy-card"><div className="legacy-card-title"><div><span>07</span><h4>{t("legacyLifeEntry.reviewBeforeSubmit")}</h4></div></div><div className="legacy-review-grid">
        <div><span>{t("legacyLifeEntry.contractNumber")}</span><b>{data.contract.contractNumber || "-"}</b></div><div><span>{t("legacyLifeEntry.entryDate")}</span><b>{data.contract.entryDate || "-"}</b></div><div><span>{t("legacyLifeEntry.insured")}</span><b>{data.customer.firstName} {data.customer.lastName}</b></div><div><span>{t("legacyLifeEntry.beneficiaries")}</span><b>{data.beneficiaries.length} {t("legacyLifeEntry.person")}</b></div><div><span>{t("legacyLifeEntry.bills")}</span><b>{data.payments.length} / {monthCount} {t("legacyLifeEntry.months")}</b></div><div><span>{t("legacyLifeEntry.amountPaid")}</span><b>{total.toLocaleString()} LAK</b></div><div><span>{t("legacyLifeEntry.documents")}</span><b>{(data.documents || []).length + files.length} {t("legacyLifeEntry.files")}</b></div>
      </div></section>

      <div className="legacy-foot legacy-foot-single"><div><button type="button" className="legacy-btn-secondary" onClick={() => save(false)}>{t("legacyLifeEntry.saveDraft")}</button><button type="button" className="legacy-btn-primary" onClick={() => save(true)}>✓ {t("legacyLifeEntry.confirmSubmit")}</button></div></div>
    </div>
  );
}
